import { useEffect, useMemo, useState } from "react";

import { buildClozeStory, CLOZE_POINTS_AFTER_MISS, CLOZE_POINTS_FIRST_TRY, findNextUnansweredKey } from "./clozeStoryHelpers";

const isAnswered = (status) => status === "correct" || status === "incorrect";

export default function useClozeStory(story, savedStatuses) {
  const clozeStory = useMemo(() => buildClozeStory(story?.paragraphs ?? []), [story?.paragraphs]);
  const clozeItems = useMemo(() => clozeStory.flat().filter((item) => item.cloze), [clozeStory]);
  const clozeKeys = useMemo(() => clozeItems.map((item) => item.key), [clozeItems]);
  const [statuses, setStatuses] = useState({});
  const [wrongPicks, setWrongPicks] = useState({});
  const [activeKey, setActiveKey] = useState(null);
  const [hasAnsweredThisSession, setHasAnsweredThisSession] = useState(false);

  const startWith = (initialStatuses) => {
    setStatuses(initialStatuses);
    setWrongPicks({});
    setActiveKey(null);
    setHasAnsweredThisSession(false);
  };

  const restart = () => startWith({});

  useEffect(() => startWith(savedStatuses), [story?.id]);

  const activeItem = clozeItems.find((item) => item.key === activeKey) ?? null;
  const statusList = clozeKeys.map((key) => statuses[key]);
  const correctCount = statusList.filter((status) => status === "correct").length;
  const answeredAfterMissCount = statusList.filter((status) => status === "incorrect").length;
  const incorrectCount = statusList.filter((status) => status === "attempted" || status === "incorrect").length;
  const isComplete = clozeKeys.length > 0 && correctCount + answeredAfterMissCount === clozeKeys.length;

  const chooseOption = (option) => {
    if (!activeItem?.cloze) return;
    const key = activeItem.key;
    if (option !== activeItem.cloze.answer) {
      setStatuses((previous) => ({ ...previous, [key]: "attempted" }));
      setWrongPicks((previous) => ({ ...previous, [key]: [...(previous[key] ?? []), option] }));
      return;
    }
    const nextStatuses = { ...statuses, [key]: statuses[key] === "attempted" ? "incorrect" : "correct" };
    setStatuses(nextStatuses);
    setHasAnsweredThisSession(true);
    const answeredKeys = new Set(clozeKeys.filter((clozeKey) => isAnswered(nextStatuses[clozeKey])));
    setActiveKey(findNextUnansweredKey(clozeKeys, key, answeredKeys));
  };

  return {
    activeItem,
    chooseOption,
    clozeCount: clozeKeys.length,
    correctCount,
    incorrectCount,
    isComplete,
    isCompletedThisSession: isComplete && hasAnsweredThisSession,
    restart,
    score: correctCount * CLOZE_POINTS_FIRST_TRY + answeredAfterMissCount * CLOZE_POINTS_AFTER_MISS,
    selectBlank: setActiveKey,
    statuses,
    story: clozeStory,
    wrongPicks
  };
}
